import { useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Route } from '../../../../route-constants';
import { AppStateValue } from '../../../AppState';
import { getCachedUsers } from '../../../shared/cache/PlatformUserCache';
import { getCachedSlaDefinitions } from '../../../shared/cache/SlaDefinitionCache';
import { platformService } from '../../../shared/services/platform.service';
import { FILTERS_STRINGS, MyWorkListViewType } from '../../../shared/utils/constants';
import { chunk } from '../../../shared/utils/helper';
import { DEFAULT_SORT_ORDER_FOR_QUERYING, getCustomPropertyQueryValue } from '../../List/ListFunctions';
import { EnumSortOrderType } from '../../toolbar/CustomViewContextProvider';
import { MyWorkContext } from '../MyWorkContextProvider';
import { getMyWorkSortOrderFromStorage, getSelectedTabFromStorage, getShowClosedFromStorage } from './myWorkListHelper';
import { startRefreshTimer } from '../../../shared/components/table/editingNow';
import { keepUnchangedSlas } from '../../../shared/utils/slaCarryOver';
import { newestOnly } from '../../../shared/utils/newestOnly';

const api = new platformService();

// Caps the TicketId IN-list size per TicketSla request regardless of configured page size (25/50/100)
const SLA_ID_BATCH_SIZE = 25;

/**
 * Every load of My Work / My Group Work's list replaces its rows - a page, tab, sort or Show Closed change, Refresh, the
 * 5-minute refresh, a cell save's reload - so only the newest is drawn, as on the ticket list (listLoads). One for every
 * hook instance (the list, its toolbar, its grid), as they all draw the one list.
 */
const myWorkLoads = newestOnly();

const useMyWorkListFunctions = (currentState: AppStateValue) => {
  const { t } = useTranslation();
  const { myWorkStates, setMyWorkStates } = useContext(MyWorkContext);
  const location = useLocation();
  // Read by the 5-minute refresh, which outlives the render that started it - so it reloads the page now shown.
  const stateRef = useRef(myWorkStates);
  stateRef.current = myWorkStates;

  const isMyGroupWork = location.pathname.includes(Route.myGroupWork.pagePath);
  
  const moveToPage = (action: string) => {
    if (action === 'next') {
      setMyWorkStates(prev => ({
        ...prev,
        myWorkList: {
          ...prev.myWorkList,
          currentPage: prev.myWorkList.currentPage + 1
        }
      }));
      getpaginatedMyWorkList({ page: myWorkStates.myWorkList.currentPage + 1, selectedTabNumber: myWorkStates.myWorkList.selectedTabId });
    } else if (action === 'previous') {
      setMyWorkStates(prev => ({
        ...prev,
        myWorkList: {
          ...prev.myWorkList,
          currentPage: prev.myWorkList.currentPage - 1
        }
      }));
      getpaginatedMyWorkList({ page: myWorkStates.myWorkList.currentPage - 1, selectedTabNumber: myWorkStates.myWorkList.selectedTabId });
    } else {
      const thisPage = parseInt(action) || 1
      setMyWorkStates(prev => ({
        ...prev,
        myWorkList: {
          ...prev.myWorkList,
          currentPage: thisPage
        }
      }));
      getpaginatedMyWorkList({ page: thisPage, selectedTabNumber: myWorkStates.myWorkList.selectedTabId });
    }
  };
  const countPerPage = (count: number) => {
    localStorage.setItem('ticketListItemPerPage', count.toString());
    setMyWorkStates(prev => ({ ...prev, myWorkList: { ...prev.myWorkList, countPerPage: count } }));
    getpaginatedMyWorkList({ page: myWorkStates.myWorkList.currentPage, count: count, selectedTabNumber: myWorkStates.myWorkList.selectedTabId });
  }

  const MYWORK_LIST_COLUMNS_QUERY_VALUE: { [key in ISortOrderBy]: string } = {
    0: 'Icon',
    1: 'Id',
    2: 'Title',
    3: 'Assignee',
    4: 'StatusName',
    5: 'GroupChat',
    6: 'RequesterName',
    7: 'TeamName',
    8: 'SupportGroupName',
    9: 'TypeName',
    10: 'CategoryName',
    11: 'PriorityName',
    12: 'LastModified',
    13: 'CreatedDate',
    14: 'DueDate',
    15: 'ResolutionDate',
    16: 'LifecycleName',
    17: 'LifecyclePhaseName',
    18: 'Sla',
    19: 'AffectedUsers',
    20: 'Collaborators',
    21: 'ResolutionCategory',
    22: 'Note',
    23: 'InternalNote',
    24: 'DraftKB',
    25: 'TotalTimeSpentMinutes',
    // My Work sends it in $orderby; the server sorts the Tickets tab by each ticket's first tag (GetMyWork).
    26: 'Tags',
    // GetMyWork sorts the single tabs on their projections and the All tab in sp_GetCombinedWorkItems.
    27: 'TemplateName',
    // The same: the single tabs' projections, the All tab's stored procedure (a task or approval by its ticket's source).
    28: 'SourceName'
  };

  const _getpaginatedTicketsPromise = async ({ page, count, selectedTabNumber, showClosed, sortOrder }: IGetpaginatedMyWorkList) => {
    const skip = page * count - count;

    const isClosed = getShowClosedFromStorage(isMyGroupWork) ?? showClosed ?? myWorkStates.showClosed;
    const selectedTabId = getSelectedTabFromStorage(isMyGroupWork) ?? selectedTabNumber ?? Number(MyWorkListViewType.All) as MyWorkListDataType;
    sortOrder = getMyWorkSortOrderFromStorage(isMyGroupWork, selectedTabId) || sortOrder || myWorkStates.sortOrder[selectedTabId] || DEFAULT_SORT_ORDER_FOR_QUERYING;
    const orderBy = sortOrder.orderBy;
    const orderType = sortOrder.orderType;

    let orderByString = MYWORK_LIST_COLUMNS_QUERY_VALUE[orderBy];
    if(!orderByString)
      orderByString = getCustomPropertyQueryValue(currentState?.customProperties, MYWORK_LIST_COLUMNS_QUERY_VALUE, orderBy);

    const taskLabel = encodeURIComponent(t('ticket-details.edit-ticket-form.task.task').replace(/'/ig, "''"));
    const approvalLabel = encodeURIComponent(t('ticket-details.edit-ticket-form.approval.approval').replace(/'/ig, "''"));

    return api.getMyWorkListData(`(ViewTypeKey=${selectedTabId},IsClosed=${isClosed},IsGroupWork=${isMyGroupWork},TaskLabel='${taskLabel}',ApprovalLabel='${approvalLabel}')?$orderby=${orderByString} ${EnumSortOrderType[orderType]}&$skip=${skip}&$top=${count}`);
  };

  const loadUsers = async (data: any[]) => {
    const users = data.map(x => [x.RequesterId, x.Assignee, ...(x.AffectedUsers ?? []), ...(x.Collaborators ?? [])]).flat().filter(x => x && x !== 0);
    return await getCachedUsers(users);
  }
  /**
   * Fetches the SLAs of `fetchFor` (every ticket row of `data` unless given) and redraws `data` - only while it is still
   * the list's rows: a slow answer for an earlier load put that load's rows back over a newer page, tab or sort.
   */
  const attachSlas = async (data: any[], fetchFor: any[] = data) => {
    try {
      const ticketIds = fetchFor.filter(x => x.ItemType === MyWorkListViewType.Tickets).map(x => x.Id);
      if (ticketIds.length === 0) return;
      const fetched = new Set(ticketIds);
      const slas = (await Promise.all(chunk(ticketIds, SLA_ID_BATCH_SIZE).map(ids =>
        api.getTicketSla(`${FILTERS_STRINGS.TICKET_SLA_FILTER_LEAN}&$filter=TicketId in (${ids.join(',')})`)
      ))).flat();

      const slaDefinitions = await getCachedSlaDefinitions(slas.map(s => s.ServiceLevelAgreementId));
      slas.forEach(s => { s.ServiceLevelAgreement = slaDefinitions.get(s.ServiceLevelAgreementId); });

      data.forEach(x => {
        if (x.ItemType === MyWorkListViewType.Tickets && fetched.has(x.Id)) {
          x.TicketSlas = slas.filter(s => s.TicketId === x.Id);
        }
      });
      setMyWorkStates(prev => prev.myWorkList.allData !== data ? prev : ({
        ...prev,
        myWorkList: {
          ...prev.myWorkList,
          allData: data
        }
      }));
    } catch (e) {
      console.error(`Error attaching SLAs`, e);
    }
  };

  const getpaginatedMyWorkList = async ({
    page = 1,
    count,
    selectedTabNumber,
    showClosed,
    sortOrder,
    hideLoading,
    afterSave
  }: IGetpaginatedMyWorkList) => {
    const overtaken = myWorkLoads.start();
    try {
      if (!hideLoading) {
        setMyWorkStates(prev => ({
          ...prev,
          myWorkList: {
            ...prev.myWorkList,
            isFetching: true
          }
        }));
      }
      count ??= myWorkStates.myWorkList.countPerPage;

      const data = (await _getpaginatedTicketsPromise({ page, count, selectedTabNumber, showClosed, sortOrder }))?.data;
      // guard clause - a newer load of the list is on its way.
      if (overtaken()) return;
      const { CountApprovals = 0, CountTickets = 0, CountTasks = 0 } = data.ItemsCount || {};
      const list = JSON.parse(data.Data);

      const users = await loadUsers(list);
      if (overtaken()) return;
      list.forEach(t => {
        t.Requester = users.find(u => u.Id == t.RequesterId);
        t.AssigneeUsers = [users.find(u => u.Id == t.Assignee)];
        t.AffectedPlatformUsers = t.AffectedUsers?.map(a => users.find(u => u.Id == a)) ?? []
        t.TicketCollaborators = t.Collaborators?.map(a => users.find(u => u.Id == a)) ?? []
      });

      const isTicket = (row: any) => row.ItemType === MyWorkListViewType.Tickets;
      attachSlas(list, afterSave ? keepUnchangedSlas(list, stateRef.current.myWorkList.allData ?? [], row => row.LastModified, isTicket) : list);
      setMyWorkStates(prev => ({
        ...prev,
        myWorkList: {
          ...prev.myWorkList,
          isFetching: false,
          isRefreshing: false,
          allData: list,
          dataCount: {
              [MyWorkListViewType.All]: CountTickets + CountTasks + CountApprovals,
              [MyWorkListViewType.Tickets]: CountTickets,
              [MyWorkListViewType.Tasks]: CountTasks,
              [MyWorkListViewType.Approvals]: CountApprovals,
          }
        }
      }));
    } catch (error) {
      // guard clause - a newer load owns the list's loading state.
      if (overtaken()) return;
      console.error('Error occurred in fetching tickets', error);
      setMyWorkStates(prev => ({ ...prev, myWorkList: { ...prev.myWorkList, isRefreshing: false } }));
    }
  };

  // Stopped with stopRefreshTimer: a tick waits while a cell is being edited (editingNow.ts).
  const setMyWorkListInterval = () => startRefreshTimer(() => {
    getpaginatedMyWorkList({ 
      page: stateRef.current.myWorkList.currentPage, 
      count: stateRef.current.myWorkList.countPerPage,
      selectedTabNumber: stateRef.current.myWorkList.selectedTabId, 
      showClosed: stateRef.current.showClosed, 
      sortOrder: stateRef.current.sortOrder[stateRef.current.myWorkList.selectedTabId],
      hideLoading: true
    });
  }, 300000);

  return {
    moveToPage,
    countPerPage,
    getpaginatedMyWorkList,
    setMyWorkListInterval
  };
};

export default useMyWorkListFunctions;
